import type {MRT_ColumnDef} from 'mantine-react-table';
import type {FC} from 'react';
import {useMemo} from 'react';
import type {AssistantMemoryResponse} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {
  DeleteMenuItem,
  TableRowActions,
} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {formatUtcShortDate} from 'src/shared/utils/formatters';
import {
  isProposedMemory,
  isStaleMemory,
  memoryKindLabel,
  memorySourceLabel,
} from './assistantMemoryFormat';

export interface AssistantMemoryNotesTableProps {
  notes: AssistantMemoryResponse[];
  isLoading: boolean;
  isError: boolean;
  emptyText: string;
  onDelete: (note: AssistantMemoryResponse) => void;
}

/**
 * Read-only table of a user's Scout memory notes for admins, with a Delete row action.
 * @registry Scout memory notes table in the Control Center user memory drawer.
 */
export const AssistantMemoryNotesTable: FC<AssistantMemoryNotesTableProps> = ({
  notes,
  isLoading,
  isError,
  emptyText,
  onDelete,
}) => {
  const columns = useMemo<MRT_ColumnDef<AssistantMemoryResponse>[]>(
    () => [
      {
        id: 'status',
        header: 'Status',
        size: 110,
        accessorFn: row => row.status,
        Cell: ({row}) =>
          isProposedMemory(row.original) ? (
            <Pill text="Suggested" color="yellow" />
          ) : (
            <Pill text="Saved" color="green" />
          ),
      },
      {
        id: 'note',
        header: 'Note',
        accessorFn: row => row.text,
        Cell: ({row}) => (
          <Stack gap={2}>
            <Text size="sm" style={{whiteSpace: 'normal'}}>
              {row.original.text}
            </Text>
            <Text size="xs" c="dimmed">
              {memoryKindLabel(row.original.kind)} -{' '}
              {memorySourceLabel(row.original.source)}
            </Text>
          </Stack>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        size: 120,
        Cell: ({row}) => (
          <Text size="sm" c="dimmed">
            {formatUtcShortDate(row.original.createdAt)}
          </Text>
        ),
      },
      {
        accessorKey: 'lastUsedAt',
        header: 'Last Used',
        size: 140,
        Cell: ({row}) => (
          <Stack gap={2}>
            <Text size="sm" c="dimmed">
              {formatUtcShortDate(row.original.lastUsedAt, 'Never')}
            </Text>
            {isStaleMemory(row.original) && (
              <Text size="xs" c="dimmed" fs="italic">
                Not used in 30 days
              </Text>
            )}
          </Stack>
        ),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'assistant-memory-admin',
    columns,
    defaultDensity: 'xs',
    defaultPagination: {pageIndex: 0, pageSize: 10},
  });

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      enableSorting: false,
      enableColumnFilters: false,
      columns,
      data: notes,
      getRowId: row => String(row.id),
      renderEmptyRowsFallback: () => (
        <Text c="dimmed" ta="center" py="xl" size="sm">
          {emptyText}
        </Text>
      ),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.Menu>
            <DeleteMenuItem onClick={() => onDelete(row.original)} />
          </TableRowActions.Menu>
        </TableRowActions>
      ),
    },
    totalCount: notes.length,
    isError,
    isLoading,
    isFetching: false,
  });

  return table;
};
